import { NGROK } from "../../../../APIs";
import { apiClient } from "../../../../utils/apiClient";

export type SiemProtocol = "TCP" | "UDP" | "TLS";
export type SiemMessageFormat = "CEF";
export type SiemTlsVersion = "AUTO" | "TLS_1_2" | "TLS_1_3";
export type SiemTlsMode = "NONE" | "TLS" | "MTLS";

export interface TenantSiemConfiguration {
    id?: number;
    tenantName?: string;
    destinationName: string;
    enabled: boolean;
    host: string;
    port: number;
    protocol: SiemProtocol;
    messageFormat: SiemMessageFormat;
    connectionTimeoutSec?: number;
    readTimeoutSec?: number;
    tlsMode?: SiemTlsMode;
    verifyServerCertificate?: boolean;
    hasCaCertificate?: boolean;
    caCertificateFilename?: string;
    hasClientKeystore?: boolean;
    clientKeystoreFilename?: string;
    hasClientKeystorePassword?: boolean;
    hasClientCertificate?: boolean;
    clientCertificateFilename?: string;
    hasClientPrivateKey?: boolean;
    clientPrivateKeyFilename?: string;
    hasPrivateKeyPassword?: boolean;
    tlsVersion?: SiemTlsVersion;
    keepAlive?: boolean;
    retryAttempts?: number;
    retryIntervalSec?: number;
    queueCapacity?: number;
    severityFilter?: string;
    eventCategoryFilter?: string;
    updatedAt?: string;
}

export interface TenantSiemConfigurationRequest {
    id?: number;
    destinationName: string;
    enabled: boolean;
    host: string;
    port: number;
    protocol: SiemProtocol;
    messageFormat: SiemMessageFormat;
    connectionTimeoutSec?: number;
    readTimeoutSec?: number;
    tlsMode?: SiemTlsMode;
    verifyServerCertificate?: boolean;
    caCertificate?: string;
    caCertificateFilename?: string;
    clearCaCertificate?: boolean;
    clientKeystore?: string;
    clientKeystoreFilename?: string;
    clearClientKeystore?: boolean;
    clientKeystorePassword?: string;
    clearClientKeystorePassword?: boolean;
    clientCertificate?: string;
    clientCertificateFilename?: string;
    clearClientCertificate?: boolean;
    clientPrivateKey?: string;
    clientPrivateKeyFilename?: string;
    clearClientPrivateKey?: boolean;
    privateKeyPassword?: string;
    clearPrivateKeyPassword?: boolean;
    tlsVersion?: SiemTlsVersion;
    keepAlive?: boolean;
    retryAttempts?: number;
    retryIntervalSec?: number;
    queueCapacity?: number;
    severityFilter?: string;
    eventCategoryFilter?: string;
}

export interface SiemTestResult {
    success: boolean;
    message: string;
}

export interface SiemFilterOptions {
    eventCategories: string[];
    severities: string[];
    sourceBuckets?: string[];
}

export interface SiemCefFieldMapping {
    sourceField: string;
    cefKey: string;
    label?: string;
}

export interface SiemCefSeverityMapping {
    name: string;
    value: number;
}

export interface TenantSiemCefMapping {
    tenantName?: string;
    vendor: string;
    product: string;
    version: string;
    defaultSeverity: number;
    fieldMappings: SiemCefFieldMapping[];
    severityMappings: SiemCefSeverityMapping[];
    updatedAt?: string;
    usingDefaults?: boolean;
}

export type TenantSiemCefMappingRequest = Omit<TenantSiemCefMapping, "tenantName" | "updatedAt" | "usingDefaults">;

const baseUrl = (tenantName: string) =>
    `${NGROK}/api/${encodeURIComponent(tenantName)}/siem-configuration`;

export const fetchSiemConfigurations = async (
    tenantName: string,
): Promise<TenantSiemConfiguration[]> => {
    return apiClient<TenantSiemConfiguration[]>({
        method: "GET",
        url: baseUrl(tenantName),
    });
};

export const fetchSiemFilterOptions = async (tenantName: string): Promise<SiemFilterOptions> => {
    return apiClient<SiemFilterOptions>({
        method: "GET",
        url: `${baseUrl(tenantName)}/filters`,
    });
};

export const fetchSiemCefMapping = async (tenantName: string): Promise<TenantSiemCefMapping> => {
    return apiClient<TenantSiemCefMapping>({
        method: "GET",
        url: `${baseUrl(tenantName)}/cef-mapping`,
    });
};

export const saveSiemCefMapping = async (
    tenantName: string,
    payload: TenantSiemCefMappingRequest,
): Promise<TenantSiemCefMapping> => {
    return apiClient<TenantSiemCefMapping>({
        method: "POST",
        url: `${baseUrl(tenantName)}/cef-mapping`,
        data: payload,
    });
};

export const saveSiemConfiguration = async (
    tenantName: string,
    payload: TenantSiemConfigurationRequest,
): Promise<TenantSiemConfiguration> => {
    return apiClient<TenantSiemConfiguration>({
        method: "POST",
        url: baseUrl(tenantName),
        data: payload,
    });
};

export const deleteSiemConfiguration = async (
    tenantName: string,
    id: number,
): Promise<boolean> => {
    return apiClient<boolean>({
        method: "DELETE",
        url: `${baseUrl(tenantName)}/${id}`,
    });
};

export const testSiemConnection = async (
    tenantName: string,
    id: number,
): Promise<SiemTestResult> => {
    return apiClient<SiemTestResult>({
        method: "POST",
        url: `${baseUrl(tenantName)}/${id}/test-connection`,
    });
};

export const sendSiemTestEvent = async (
    tenantName: string,
    id: number,
): Promise<SiemTestResult> => {
    return apiClient<SiemTestResult>({
        method: "POST",
        url: `${baseUrl(tenantName)}/${id}/test-event`,
    });
};
